import React, { useRef } from 'react';
import CategoryHeader from './CategoryHeader';
import { randomSize, reviewStatusBadgeCls, reviewStatusLabel } from './utils';
import { DOCUMENT_FILE_ACCEPT } from '../../../../utils/documentUploadPolicy';

const EMPLOYED_SUBCATS = [
  { key: 'contract', label: 'Work contract' },
  { key: 'payslip', label: 'Payslip' },
  { key: 'vidalaboral', label: 'Vida laboral' },
  { key: 'joboffer', label: 'Job offer letter' },
  { key: 'other', label: 'Other documents' },
];

const SELF_EMPLOYED_SUBCATS = [
  { key: 'other', label: 'Other documents' },
];

export default function EmploymentCategory({
  docs,
  onDocsChange,
  employmentFields,
  onEmploymentFieldsChange,
  categoryReview,
  onCategoryReviewChange,
  onViewDocument,
}) {
  const fileRefs = useRef({});
  const workType = employmentFields?.workType || 'employed';
  const isSelfEmployed = workType === 'selfemployed';
  const visibleSubcats = isSelfEmployed ? SELF_EMPLOYED_SUBCATS : EMPLOYED_SUBCATS;

  const docsFor = (subKey) =>
    docs.filter((d) => d.category === 'employment' && d.docType === subKey && d.uploaded);

  const handleFile = (subKey, file) => {
    onDocsChange([
      ...docs,
      {
        id: `emp-${Date.now()}`,
        name: file.name,
        category: 'employment',
        docType: subKey,
        uploaded: true,
        uploadedAt: new Date().toISOString().slice(0, 10),
        uploadedFile: file.name,
        rawFile: file,
        reviewStatus: 'pending',
        fileSize: randomSize(),
      },
    ]);
  };

  const handleRemove = (id) => onDocsChange(docs.filter((d) => d.id !== id));

  return (
    <div className="max-w-[720px]">
      <CategoryHeader title="Employment" category="employment" status={categoryReview} onChange={onCategoryReviewChange} />

      <div className="mb-5 grid grid-cols-1 gap-2 max-w-[420px] min-[420px]:grid-cols-2">
        <button
          type="button"
          onClick={() => onEmploymentFieldsChange?.({ workType: 'employed' })}
          className={`min-h-[44px] rounded-md border text-sm font-semibold transition-all ${
            !isSelfEmployed
              ? 'bg-emerald-50 border-emerald-400 text-emerald-800'
              : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50 cursor-pointer'
          }`}
        >
          Employed
        </button>
        <button
          type="button"
          onClick={() => onEmploymentFieldsChange?.({ workType: 'selfemployed' })}
          className={`min-h-[44px] rounded-md border text-sm font-semibold transition-all ${
            isSelfEmployed
              ? 'bg-emerald-50 border-emerald-400 text-emerald-800'
              : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50 cursor-pointer'
          }`}
        >
          Self-employed
        </button>
      </div>

      {isSelfEmployed ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-6 max-w-[480px]">
          <div className="mb-4">
            <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">Describe your activity</label>
            <div className="w-full min-h-[40px] px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700">
              {employmentFields?.selfEmploymentActivity?.trim() || 'Not provided'}
            </div>
          </div>
          <p className="text-[12px] text-slate-500 leading-relaxed">
            The activity description and any brief document description are read only here.
          </p>
        </div>
      ) : null}

      {visibleSubcats.map((sub) => (
        <div key={sub.key} className="mb-5 max-w-[480px]">
          <p className="text-[14px] font-semibold text-slate-800 mt-5 mb-2">{sub.label}</p>

          {docsFor(sub.key).map((doc) => (
            <div key={doc.id} className="bg-emerald-50 rounded-lg px-3 py-2 flex flex-col gap-2 mb-2 min-[420px]:flex-row min-[420px]:items-center">
              <i className="ri-file-check-line text-emerald-500 text-sm shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="min-w-0 text-[12px] font-medium text-emerald-800 truncate">{doc.uploadedFile}</p>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${reviewStatusBadgeCls(doc.reviewStatus)}`}>
                    {reviewStatusLabel(doc.reviewStatus)}
                  </span>
                </div>
                {doc.supportingDescription ? (
                  <p className="mt-0.5 text-[11px] text-slate-600 truncate">
                    Brief description: {doc.supportingDescription}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => onViewDocument?.(doc)}
                className="min-h-[36px] text-[11px] text-slate-600 font-medium border border-slate-200 rounded-md px-2 py-1 cursor-pointer shrink-0 whitespace-nowrap bg-white hover:bg-slate-50"
              >
                View
              </button>
              <button
                type="button"
                onClick={() => fileRefs.current[sub.key]?.click()}
                className="min-h-[36px] text-[11px] text-emerald-600 font-medium underline cursor-pointer shrink-0"
              >
                Change
              </button>
              <button
                type="button"
                onClick={() => handleRemove(doc.id)}
                className="w-9 h-9 flex items-center justify-center text-slate-300 hover:text-rose-500 cursor-pointer shrink-0"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
            </div>
          ))}

          {docsFor(sub.key).length === 0 ? (
            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={() => fileRefs.current[sub.key]?.click()}
                className="min-h-[44px] flex items-center justify-center gap-1.5 bg-white border-2 border-dashed border-slate-200 rounded-xl font-medium text-[13px] text-slate-500 hover:border-slate-300 cursor-pointer"
              >
                <i className="ri-file-upload-line text-sm" /> Choose file
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRefs.current[sub.key]?.click()}
              className="w-full min-h-[44px] flex items-center justify-center gap-2 bg-white border-2 border-dashed border-slate-200 rounded-xl font-medium text-[13px] text-slate-500 hover:border-slate-300 cursor-pointer"
            >
              <i className="ri-add-line text-base" /> Add another
            </button>
          )}

          <input
            ref={(el) => {
              fileRefs.current[sub.key] = el;
            }}
            type="file"
            accept={DOCUMENT_FILE_ACCEPT}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(sub.key, file);
              if (e.target) e.target.value = '';
            }}
          />
        </div>
      ))}
    </div>
  );
}
